import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { MEAL_OPTIONS, MealTime } from './FoodRegistrationFlow';

interface FoodLogRow {
  id: number;
  item_name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  meal_type: string;
  log_date: string;
}

interface Props {
  foodLogs: FoodLogRow[];
  onDelete?: (id: number) => void;
  onAddToMeal?: (mealTime: MealTime) => void;
}

export function MealSummaryView({ foodLogs, onDelete, onAddToMeal }: Props) {
  const [expandedMeals, setExpandedMeals] = useState<Set<string>>(new Set(MEAL_OPTIONS.map(m => m.value)));

  const toggleMeal = (meal: string) => {
    setExpandedMeals(prev => {
      const next = new Set(prev);
      if (next.has(meal)) next.delete(meal); else next.add(meal);
      return next;
    });
  };

  // Group logs by meal_type
  const grouped = MEAL_OPTIONS.map(meal => ({
    ...meal,
    items: foodLogs.filter(f => f.meal_type === meal.value),
    totalCals: foodLogs.filter(f => f.meal_type === meal.value).reduce((s, f) => s + Number(f.calories), 0),
    totalProtein: foodLogs.filter(f => f.meal_type === meal.value).reduce((s, f) => s + Number(f.protein || 0), 0),
    totalCarbs: foodLogs.filter(f => f.meal_type === meal.value).reduce((s, f) => s + Number(f.carbs || 0), 0),
    totalFat: foodLogs.filter(f => f.meal_type === meal.value).reduce((s, f) => s + Number(f.fat || 0), 0),
  }));

  // Also group any logs with legacy meal_types
  const knownTypes = MEAL_OPTIONS.map(m => m.value) as string[];
  const otherLogs = foodLogs.filter(f => !knownTypes.includes(f.meal_type));
  if (otherLogs.length > 0) {
    grouped.push({
      value: 'otro' as MealTime,
      label: 'Otro',
      emoji: '🍴',
      items: otherLogs,
      totalCals: otherLogs.reduce((s, f) => s + Number(f.calories), 0),
      totalProtein: otherLogs.reduce((s, f) => s + Number(f.protein || 0), 0),
      totalCarbs: otherLogs.reduce((s, f) => s + Number(f.carbs || 0), 0),
      totalFat: otherLogs.reduce((s, f) => s + Number(f.fat || 0), 0),
    });
  }

  const activeMeals = grouped.filter(g => g.items.length > 0);
  const emptyMeals = grouped.filter(g => g.items.length === 0);

  return (
    <div className="space-y-3">
      {activeMeals.map(meal => (
        <div key={meal.value} className="bg-card border-2 border-secondary rounded-xl overflow-hidden">
          <button
            onClick={() => toggleMeal(meal.value)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{meal.emoji}</span>
              <div className="text-left">
                <span className="font-black text-sm uppercase">{meal.label}</span>
                <span className="text-xs text-muted-foreground ml-2">({meal.items.length})</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-black text-accent text-sm">{meal.totalCals} kcal</span>
              {expandedMeals.has(meal.value) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>

          {expandedMeals.has(meal.value) && (
            <div className="px-4 pb-3 space-y-2">
              {meal.items.map(item => (
                <div key={item.id} className="flex items-center justify-between bg-secondary/50 rounded-lg p-3 group">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{item.item_name}</div>
                    <div className="flex gap-3 mt-1">
                      <span className="text-[10px] font-bold text-accent">{Number(item.calories)} kcal</span>
                      {Number(item.protein) > 0 && <span className="text-[10px] font-bold text-blue-400">P: {Number(item.protein)}g</span>}
                      {Number(item.carbs) > 0 && <span className="text-[10px] font-bold text-yellow-400">C: {Number(item.carbs)}g</span>}
                      {Number(item.fat) > 0 && <span className="text-[10px] font-bold text-orange-400">G: {Number(item.fat)}g</span>}
                    </div>
                  </div>
                  {onDelete && (
                    <button
                      onClick={() => onDelete(item.id)}
                      aria-label="Eliminar este alimento"
                      title="Eliminar este alimento"
                      className="text-muted-foreground hover:text-destructive transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}

              {/* Meal subtotals */}
              <div className="flex gap-3 pt-2 border-t border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">
                <span>P: {Math.round(meal.totalProtein)}g</span>
                <span>C: {Math.round(meal.totalCarbs)}g</span>
                <span>G: {Math.round(meal.totalFat)}g</span>
              </div>

              {onAddToMeal && (
                <button
                  onClick={() => onAddToMeal(meal.value)}
                  className="w-full text-xs text-accent font-bold py-2 hover:bg-accent/10 rounded-lg transition-colors"
                >
                  + Agregar a {meal.label}
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Empty meals — compact row to add */}
      {emptyMeals.length > 0 && onAddToMeal && (
        <div className="flex flex-wrap gap-2">
          {emptyMeals.map(meal => (
            <button
              key={meal.value}
              onClick={() => onAddToMeal(meal.value)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-border text-muted-foreground hover:border-accent/50 hover:text-accent transition-all text-xs font-bold"
            >
              <span>{meal.emoji}</span> {meal.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
